import { memo, useCallback, useEffect, useState } from 'react'
import styles from '../positions/index.module.scss'
import { Button, Modal } from 'antd'
import {
    createLeagueDivision,
    deleteLeagueDivision,
    editLeagueDivision,
    getLeagueDivisionById,
    getLeagueDivisions,
} from 'services/league-divisions'
import { LeagueDivision } from 'types/league-divisions'
import { LeagueDivisionsTable } from '../../components/league-divisions-table'
import { LeagueDivisionForm } from '../../components/league-division-form'

const LeagueDivisions = memo(() => {
    const [divisions, setDivisions] = useState<LeagueDivision[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [division, setDivision] = useState<LeagueDivision>({} as LeagueDivision)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getLeagueDivisions()
        }

        getAll()
            .then(data => {
                setDivisions(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setDivision({} as LeagueDivision)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getLeagueDivisionById(id)
                .then(item => {
                    setDivision(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteLeagueDivision(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: LeagueDivision) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (division.id) {
                editLeagueDivision({
                    ...submitValues,
                    id: division.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createLeagueDivision(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, division, toggleModal]
    )

    const editMode = !!division.id
    const modalTitle = `${editMode ? `Edit league division ${division.name}` : `Add new league division`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            League Divisions
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <LeagueDivisionsTable
                data={divisions}
                onEdit={handleEdit}
                onDelete={handleDelete}
                loading={somethingLoading}
            />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <LeagueDivisionForm leagueDivision={division} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})

export default LeagueDivisions
